import { makeStyles } from '@material-ui/core/styles'
import {
  coachProfileBandOuter,
  coachProfileBandSectionGap,
  coachProfileContentCanvas,
  coachProfileSectionHeading,
} from '@lib/pga-coach/coach-profile-layout'

const useStyles = makeStyles((theme) => ({
  /**
   * Full-bleed testimonial band on the same white canvas as the rest
   * of the flagged profile. Section title matches Photos / Services /
   * Request Coaching (h5 + fontWeight 800 + navy). Quote cards are body
   * type with a light customer-comment treatment — not a serif
   * pull-quote, gold uppercase cite, or blue overline.
   *
   * Wraps the existing `testimonialsJsonData` data shape so the upstream
   * `TestimonialV2` core component is not modified.
   */
  root: ({ inColumn }) => ({
    /**
     * Band-level top gap is `coachProfileBandSectionGap` (48px) — the
     * Photos→Testimonials stacked space. Padding bottom stays 0 so a
     * following content-column item does not double the gap.
     */
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    ...coachProfileBandOuter(theme, {
      inColumn,
      fullBleed: !inColumn,
      ...coachProfileBandSectionGap(theme),
      paddingBottom: 0,
    }),
  }),
  inner: ({ inColumn }) => ({
    ...coachProfileContentCanvas({ inColumn }),
    textAlign: 'left',
  }),
  /**
   * Same computed type as other flagged band titles
   * (`coachProfileSectionHeading`).
   */
  heading: {
    ...coachProfileSectionHeading(theme),
    margin: 0,
    marginBottom: theme.spacing(3),
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  item: {
    margin: 0,
    padding: 0,
  },
  /**
   * Modest quote card: light fill, 3px left rule, compact padding.
   * No drop shadow and no oversized quotation glyph.
   */
  blockquote: {
    margin: 0,
    padding: theme.spacing(2, 2.5),
    borderLeft: `3px solid ${theme.palette.grey.light}`,
    backgroundColor: theme.palette.blue.light25,
    borderRadius: 4,
  },
  /**
   * Curly quotes are added via `::before` / `::after` so the underlying
   * DOM text node stays equal to the raw testimonial string. Lets
   * `getByText('Great coach!')`-style assertions in coaches-page tests
   * keep working without modification.
   */
  quote: {
    margin: 0,
    color: theme.palette.text.primary,
    '&::before': {
      content: '"\\201C"',
    },
    '&::after': {
      content: '"\\201D"',
    },
  },
  attribution: {
    display: 'block',
    fontStyle: 'italic',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
}))

export default useStyles
